import { useState } from "react";

import { PageHeader } from "../shell/PageHeader";
import { SiteTabBar } from "../components/SiteTabBar";
import { HourlySourceMix } from "@solarkey/charts";
import { EmptyState } from "@solarkey/ui";

/* ── Screen 21 (P2, C&I): Site: Now for a commercial site with storage and a genset ──
   Same page as the utility Site: Now (screen 16); a commercial site's asset graph has
   storage and genset nodes, so the view model adds the balance tiles, the storage card
   and the hourly source mix. SiteNow delegates here when site_class === "commercial"
   AND the payload actually carries this composition.

   The backend serves NONE of these objects today (no storage, genset or load meter is
   registered for any site), so every field below arrives only from a dev fixture. The
   screen therefore checks its input first: a missing object renders an honest
   "not available for this site" state instead of reading `undefined.scenario` and
   white-screening the app (there is no error boundary above it). */

type Money = { value: number; currency: string };
type Tariff = { value: number; currency: string; per: string };

type BalanceTile = {
  key: string; label: string; value: number | string; unit: string;
  sub?: string; prov?: string; prov_lead?: boolean; lead?: string;
};
type BusSegment = { kind: string; label: string; value?: number; unit?: string; flex?: number };

export type CommercialSiteNow = {
  site_header: { name: string; slug: string; scope_line: string };
  balance_tiles: BalanceTile[];
  figures_strip: { label: string; value: string; unit: string; sub: string }[];
  bus: { scenario: string; scenarios: string[]; feed: BusSegment[]; draw: BusSegment[] };
  storage_card: {
    spec: string; state: string; provenance: string;
    tiles: { label: string; value: number; unit: string; sub: string }[];
    genset: { spec: string; state: string; last_run: string; fuel_measured: boolean };
  };
  load_sources: {
    unit: string; hours: string[];
    sources: { name: string; label: string; values: number[] }[];
    load: number[]; load_label: string; note: string;
  };
  savings: { avoided_cost: Money; summary: string; tariff: Tariff };
  contract_demand_locked: { title: string; detail: string; tier: string };
};

/** Every object this screen reads, present. Checked before anything is rendered —
 *  a partial payload is treated as absent, never as zeros. */
function isComplete(data?: Partial<CommercialSiteNow> | null): data is CommercialSiteNow {
  return !!(
    data &&
    data.site_header?.name &&
    Array.isArray(data.balance_tiles) &&
    Array.isArray(data.figures_strip) &&
    data.bus?.scenario &&
    Array.isArray(data.bus.scenarios) &&
    Array.isArray(data.bus.feed) &&
    Array.isArray(data.bus.draw) &&
    data.storage_card?.genset &&
    Array.isArray(data.storage_card.tiles) &&
    Array.isArray(data.load_sources?.hours) &&
    Array.isArray(data.load_sources?.sources) &&
    Array.isArray(data.load_sources?.load) &&
    typeof data.savings?.avoided_cost?.value === "number" &&
    !!data.savings.avoided_cost.currency &&
    typeof data.savings.tariff?.value === "number" &&
    !!data.savings.tariff.currency &&
    data.contract_demand_locked
  );
}

export function SiteNowCommercial(
  { site, data }: { site: string; data?: Partial<CommercialSiteNow> | null },
): React.ReactNode {
  // The hook must run on every render, so the scenario state is initialised
  // defensively and the completeness check happens after it.
  const [scenario, setScenario] = useState(data?.bus?.scenario ?? "");

  if (!isComplete(data)) {
    return (
      <div className="flex flex-col gap-[14px]">
        <PageHeader
          title={data?.site_header?.name ?? site}
          scope="Commercial (C&I) composition"
          showTimeControl={false}
        />
        <SiteTabBar site={site} />
        <section className="sk-section">
          <EmptyState
            variant="no_data"
            title="The commercial view is not available for this site"
            message="This composition needs storage, genset and load-meter data. The platform holds none for this site, so nothing on it can be stated. The site's standard figures are on the other tabs."
          />
        </section>
      </div>
    );
  }

  const header = data.site_header;
  const storage = data.storage_card;
  const ls = data.load_sources;
  const savings = data.savings;
  const lock = data.contract_demand_locked;
  // The served default until the reader picks a scenario.
  const activeScenario = scenario || data.bus.scenario;

  return (
    <div className="flex flex-col gap-[14px]">
      <PageHeader
        title={header.name}
        scope={header.scope_line}
        timeControl="mode"
        action={
          <button type="button" style={woLockedStyle} disabled aria-disabled="true">
            <span style={{ marginRight: 4 }} aria-hidden>🔒</span>
            Raise work order · Easy T
          </button>
        }
      />
      <SiteTabBar site={site} />

      {/* ── Row 1: six balance tiles (annotation ①) ── */}
      <section aria-labelledby="cmrc-tiles">
        <h2 id="cmrc-tiles" className="sr-only">Balance now</h2>
        <div className="sk-tiles-6">
          {data.balance_tiles.map((t) => (
            <div key={t.key} className="sk-tile">
              <span className="sk-tile__label">{t.label}</span>
              <span className="sk-metric__value">
                {t.value}
                <span className="sk-metric__unit">{t.unit}</span>
              </span>
              <span className="sk-tile__sub">{tileSub(t)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Figures strip: six balance reference figures ── */}
      <div className="sk-kvstrip">
        {data.figures_strip.map((f) => (
          <div key={f.label} className="sk-kvstrip__item">
            <span className="sk-kvstrip__label">{f.label}</span>
            <span className="sk-kvstrip__val">{f.value}<small>{f.unit}</small></span>
            <span className="sk-kvstrip__sub">{f.sub}</span>
          </div>
        ))}
      </div>

      {/* ── Row 2: Power flow (1.15fr) + Storage & genset (1fr) ── */}
      <div className="sk-cmrc-cols">
        <section aria-labelledby="cmrc-flow" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="cmrc-flow" className="sk-section__title">Power flow</h2>
              <span className="sk-section__sub">now · what feeds the bus, what draws from it</span>
            </div>
            <div className="sk-scenario" role="group" aria-label="Scenario">
              {data.bus.scenarios.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-active={activeScenario.toLowerCase() === s.toLowerCase() ? "true" : "false"}
                  onClick={() => setScenario(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="sk-flowbus" style={{ height: 150 }}>
            <div className="sk-flowcol">
              {data.bus.feed.map((seg, i) => <BusSeg key={`f-${i}`} seg={seg} />)}
            </div>
            <div className="sk-flowbar" aria-hidden="true">Site bus</div>
            <div className="sk-flowcol">
              {data.bus.draw.map((seg, i) => <BusSeg key={`d-${i}`} seg={seg} />)}
            </div>
          </div>
          <p className="text-xs text-text-muted" style={{ lineHeight: 1.4, margin: 0 }}>
            Feeding in on the left, drawing out on the right; the two sides balance to the
            kilowatt. A source that is fitted and idle is drawn dashed with its state; one
            that is not fitted says so.
          </p>
        </section>

        <section aria-labelledby="cmrc-storage" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="cmrc-storage" className="sk-section__title">Storage</h2>
              <span className="sk-section__sub">{storage.spec}</span>
            </div>
            <span className="sk-stor-state" data-state={storage.state}>
              <span className="sk-stor-state__dot" />{storage.state}
            </span>
          </div>
          <div className="sk-stor-grid">
            {storage.tiles.map((t) => (
              <div key={t.label} className="sk-stor-tile">
                <span className="sk-tile__label">{t.label}</span>
                <span className="sk-metric__value">
                  {t.value}
                  <span className="sk-metric__unit">{t.unit}</span>
                </span>
                <span className="sk-tile__sub">{t.sub}</span>
              </div>
            ))}
          </div>
          <p className="sk-genset-line">
            All eight are <span className="sk-basis-chip">{storage.provenance}</span> by the
            battery management system. <b>Genset</b> · {storage.genset.spec} · {storage.genset.state}
            {" · "}{storage.genset.last_run} · fuel{" "}
            {storage.genset.fuel_measured
              ? <span>measured</span>
              : <span className="sk-stateword-sm">Not measured</span>}
          </p>
        </section>
      </div>

      {/* ── Row 3: Where the load was met from (1.6fr) + Savings / lock (1fr) ── */}
      <div className="sk-cmrc-cols-wide">
        <section aria-labelledby="cmrc-loadmix" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="cmrc-loadmix" className="sk-section__title">Where the load was met from</h2>
              <span className="sk-section__sub">today · energy per completed hour · one unit</span>
            </div>
          </div>
          <HourlySourceMix
            hours={ls.hours}
            sources={ls.sources}
            unit={ls.unit}
            load={ls.load}
            loadLabel={ls.load_label}
            height={200}
          />
          <p className="text-xs text-text-muted" style={{ margin: 0 }}>{ls.note}</p>
        </section>

        <div className="flex flex-col gap-[14px]">
          <section aria-labelledby="cmrc-savings" className="sk-section">
            <div className="sk-section__head">
              <div>
                <h2 id="cmrc-savings" className="sk-section__title">Savings</h2>
                <span className="sk-section__sub">Value lens · avoided cost basis</span>
              </div>
            </div>
            <div className="sk-savings">
              <span className="sk-tile__label">Avoided cost today</span>
              <span className="sk-savings__val">{fmtMoney(savings.avoided_cost)}</span>
              <span className="sk-tile__sub">
                {savings.summary}{" "}
                <span className="sk-basis-chip">{fmtTariff(savings.tariff)}</span>
              </span>
            </div>
          </section>
          <div className="sk-lockbox">
            <span aria-hidden style={{ marginRight: 6 }}>🔒</span>
            <span><b>{lock.title}</b> · {lock.detail} · {lock.tier}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function BusSeg({ seg }: { seg: BusSegment }): React.ReactNode {
  if (seg.kind === "empty") {
    return (
      <div className="sk-flowseg" data-empty="true" style={{ flex: seg.flex ?? 1 }}>
        <span className="sk-flowseg__note">{seg.label}</span>
      </div>
    );
  }
  return (
    <div className="sk-flowseg" data-kind={seg.kind} style={{ flex: seg.flex ?? 2 }}>
      <span className="sk-flowseg__kind">{seg.label}</span>
      <span className="sk-flowseg__val">
        {seg.value}
        <span style={{ fontSize: "0.7rem", marginLeft: 2, fontWeight: 400 }}>{seg.unit}</span>
      </span>
    </div>
  );
}

/** Build a tile's sub-line, matching the wireframe's chip placement per tile. */
function tileSub(t: BalanceTile): React.ReactNode {
  const chip = t.prov ? <span className="sk-basis-chip">{t.prov}</span> : null;
  if (t.prov_lead) return <>{chip} {t.sub}</>;              // "[measured] load meter"
  if (t.lead) return <>{t.lead} · {chip}</>;                // "exporting · [measured]"
  if (t.sub && chip) return <>{t.sub} · {chip}</>;          // "74% of AC · [reported]"
  return <>{t.sub}</>;                                      // plain definition line
}

function fmtMoney(m: Money): string {
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: m.currency, maximumFractionDigits: 0 }).format(m.value);
  } catch { return `${m.value}`; }
}

function fmtTariff(t: Tariff): string {
  try {
    const v = new Intl.NumberFormat("en-IN", { style: "currency", currency: t.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(t.value);
    return `${v}/${t.per}`;
  } catch { return `${t.value}/${t.per}`; }
}

const woLockedStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  height: 30,
  padding: "0 12px",
  fontSize: "12px",
  fontWeight: 600,
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-control)",
  background: "var(--sk-color-surface-1)",
  color: "var(--sk-color-ink-3)",
  cursor: "not-allowed",
  whiteSpace: "nowrap",
};
